// Ship on the handheld: pick an order from the queue, scan its drones, record, then offer the
// next one. Drones coming back are Marker's Mark returned; `onReturn` opens it from here.

import React, { useEffect, useRef, useState } from 'react';
import { BTN_TONAL, FOCUS, MOTION } from '../../Requisitions/buttonStyles';
import { BackIcon, CheckIcon, WarningIcon } from '../icons';
import { BAR_KEY } from '../ui';
import ShipQueue from './ShipQueue';
import ShipScan from './ShipScan';
import type { QueueOrder, Recorded, ScanDirty } from './types';

type Step =
  | { name: 'queue' }
  | { name: 'scan'; order: QueueOrder }
  | { name: 'done'; recorded: Recorded };

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** What the server's reading says is left after this shipment, in the operator's words. */
function leftOver({ result }: Recorded): string {
  const owed = result.shipping.lines.reduce((n, l) => n + Math.max(l.remaining, 0), 0);
  return owed > 0 ? `${owed} still to ship on this order.` : 'Everything on this order has shipped.';
}

const ReturnKey: React.FC<{ onReturn: () => void }> = ({ onReturn }) => (
  <button type="button" onClick={onReturn} className={`${BTN_TONAL} min-h-[2.75rem] w-full justify-center`}>
    Drones coming back? Mark returned
  </button>
);

const Done: React.FC<{ recorded: Recorded; onNext: () => void; onReturn?: () => void }> = ({ recorded, onNext, onReturn }) => {
  const { order, total, result } = recorded;
  const moved = result.status && result.status !== order.status;
  return (
    <div className="mx-auto flex w-full max-w-md flex-col gap-5 px-4 pt-6">
      <div className="flex flex-col items-center gap-3 rounded-xl bg-surface-container-low p-6 text-center motion-safe:animate-rowLand">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/15 text-primary">
          <CheckIcon className="h-8 w-8" />
        </span>
        <p className="text-xl font-semibold text-on-surface" role="status">
          Shipped {total} on {order.so_number}
        </p>
        <p className="text-base text-on-surface-variant">{order.customer}</p>
        <p className="text-base text-on-surface">{leftOver(recorded)}</p>
        {moved && (
          <p className="text-sm text-on-surface-variant">The order now reads {result.status.replace(/_/g, ' ')}.</p>
        )}
      </div>
      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={onNext}
          className={`min-h-[3.25rem] w-full rounded-full bg-primary px-5 text-base font-bold text-on-primary np-state np-state-on-primary ${FOCUS} ${MOTION}`}
        >
          Ship another
        </button>
        {onReturn && <ReturnKey onReturn={onReturn} />}
      </div>
    </div>
  );
};

const StepBar: React.FC<{ backLabel: string; onBack: () => void; title: React.ReactNode }> = ({ backLabel, onBack, title }) => (
  <div className="flex h-12 shrink-0 items-center gap-2 px-2">
    <button type="button" onClick={onBack} className={`${BAR_KEY} pl-1.5 pr-3 text-on-surface-variant hover:text-on-surface`}>
      <BackIcon className="h-5 w-5" />
      {backLabel}
    </button>
    <p className="min-w-0 truncate text-base font-semibold text-on-surface">{title}</p>
  </div>
);

/** Asked before the scan screen is left with work on it. Keep scanning is the default and holds focus. */
const DiscardPanel: React.FC<{ serials: number; onKeep: () => void; onDiscard: () => void }> = ({ serials, onKeep, onDiscard }) => {
  const keepRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    keepRef.current?.focus();
  }, []);
  const title = serials > 0 ? `Discard ${plural(serials, 'scanned drone', 'scanned drones')}?` : 'Discard your changes?';
  return (
    <div className="absolute inset-0 z-10 flex items-end bg-[rgb(25_25_25/0.8)] px-4 pb-4" onKeyDown={(e) => e.key === 'Escape' && onKeep()}>
      <div role="alertdialog" aria-modal="true" aria-label={title} className="mx-auto flex w-full max-w-md flex-col gap-3 rounded-xl bg-surface-container-high p-3">
        <p className="flex items-center gap-2 text-base font-semibold text-red-100">
          <WarningIcon className="h-5 w-5 shrink-0" />
          {title}
        </p>
        <p className="text-sm text-on-surface-variant">Going back drops what is on this screen. Nothing has been recorded.</p>
        <button
          ref={keepRef}
          type="button"
          onClick={onKeep}
          className={`min-h-[3.25rem] w-full rounded-full bg-primary px-5 text-base font-bold text-on-primary np-state np-state-on-primary ${FOCUS} ${MOTION}`}
        >
          Keep scanning
        </button>
        <button type="button" onClick={onDiscard} className={`${BTN_TONAL} min-h-[2.75rem] w-full justify-center`}>
          Discard and go back
        </button>
      </div>
    </div>
  );
};

const ShipFlow: React.FC<{
  /** The badged operator; sent as operator_id only on a shared device. */
  operatorId?: string;
  isSharedMode?: boolean;
  setLeaveGuard?: (guard: ((leave: () => void) => boolean) | null) => void;
  /** Opens Mark returned. The shortcut key shows only when this is given. */
  onReturn?: () => void;
}> = ({ operatorId, isSharedMode = false, setLeaveGuard, onReturn }) => {
  const [step, setStep] = useState<Step>({ name: 'queue' });

  const [scanState, setScanState] = useState<ScanDirty>({ serials: 0, dirty: false });
  const [asking, setAsking] = useState(false);
  // Where Discard goes: back to the order list, or out of the tool when the shell's Tools key asked.
  const pendingLeave = useRef<(() => void) | null>(null);
  const dirtyRef = useRef(false);
  dirtyRef.current = step.name === 'scan' && scanState.dirty;

  useEffect(() => {
    if (!setLeaveGuard) return undefined;
    setLeaveGuard((leave) => {
      if (!dirtyRef.current) return true;
      pendingLeave.current = leave;
      setAsking(true);
      return false;
    });
    return () => setLeaveGuard(null);
  }, [setLeaveGuard]);

  if (step.name === 'queue') {
    return (
      <div className="mx-auto w-full max-w-md flex-1 overflow-y-auto pb-6">
        <div className="flex flex-col gap-4 px-4 pt-2">
          <ShipQueue onPick={(order) => setStep({ name: 'scan', order })} />
          {onReturn && <ReturnKey onReturn={onReturn} />}
        </div>
      </div>
    );
  }

  if (step.name === 'scan') {
    return (
      <div className="relative flex min-h-0 flex-1 flex-col">
        <StepBar
          backLabel="Orders"
          onBack={() => {
            pendingLeave.current = null;
            if (scanState.dirty) setAsking(true); else setStep({ name: 'queue' });
          }}
          title={<>{step.order.customer} <span className="font-mono text-sm font-normal text-on-surface-variant">{step.order.so_number}</span></>}
        />
        <ShipScan
          order={step.order}
          operatorId={operatorId}
          isSharedMode={isSharedMode}
          onDirty={setScanState}
          paused={asking}
          onRecorded={(result, total) => setStep({ name: 'done', recorded: { order: step.order, total, result } })}
        />
        {asking && (
          <DiscardPanel
            serials={scanState.serials}
            onKeep={() => {
              pendingLeave.current = null;
              setAsking(false);
            }}
            onDiscard={() => {
              const leave = pendingLeave.current;
              pendingLeave.current = null;
              setAsking(false);
              if (leave) leave(); else setStep({ name: 'queue' });
            }}
          />
        )}
      </div>
    );
  }

  return <Done recorded={step.recorded} onNext={() => setStep({ name: 'queue' })} onReturn={onReturn} />;
};

export default ShipFlow;
